/**
 * Lightweight CoinGecko price cache.
 *
 * - Fetches at most once every CACHE_TTL_MS (5 min).
 * - On HTTP 429 falls back to the last good prices.
 * - Pure-math helpers: usdToCoin / coinToUsd — no crypto-convert.
 */

const CACHE_TTL_MS = 5 * 60 * 1000;

const COINGECKO_IDS = {
  BTC: "bitcoin",
  ETH: "ethereum",
  BNB: "binancecoin",
  TRX: "tron",
  USDT: "tether",
  USDC: "usd-coin",
  SOL: "solana",
  ARB: "arbitrum",
  AVAX: "avalanche-2",
  XRP: "ripple",
  DOGE: "dogecoin",
  POLY: "matic-network",
};

const COINGECKO_URL =
  "https://api.coingecko.com/api/v3/simple/price" +
  "?ids=bitcoin%2Cethereum%2Cbinancecoin%2Ctron%2Ctether%2Csolana%2Carbitrum%2Cripple%2Cdogecoin%2Cmatic-network%2Cusd-coin%2Cavalanche-2" +
  "&vs_currencies=usd&include_24hr_change=true";

let cachedPrices = null;
let lastFetchedAt = 0;
let inflight = null;

function normalizeTicker(ticker) {
  const t = String(ticker || "").toUpperCase();
  if (t.startsWith("USDT")) return "USDT";
  if (t.startsWith("USDC")) return "USDC";
  if (t === "MATIC" || t === "POLYGON") return "POLY";
  return t;
}

function mapResponse(data) {
  const result = {};
  for (const [ticker, geckoId] of Object.entries(COINGECKO_IDS)) {
    const entry = data[geckoId];
    if (entry) {
      result[ticker] = {
        usd: entry.usd ?? 1,
        usd_24h_change: entry.usd_24h_change ?? 0,
      };
    }
  }
  return result;
}

/**
 * Always resolves. On 429 / network error returns last good cache.
 */
export async function fetchPrices() {
  const now = Date.now();

  if (cachedPrices && now - lastFetchedAt < CACHE_TTL_MS) {
    return { prices: cachedPrices, raw: null, fromCache: true };
  }

  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch(COINGECKO_URL);
      if (res.status === 429) {
        console.warn("[priceCache] CoinGecko 429 — using stale prices");
        return {
          prices: cachedPrices,
          raw: null,
          fromCache: true,
          rateLimited: true,
        };
      }
      if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`);
      const data = await res.json();
      cachedPrices = mapResponse(data);
      lastFetchedAt = Date.now();
      return { prices: cachedPrices, raw: data, fromCache: false };
    } catch (err) {
      console.error("[priceCache] fetch error:", err);
      return { prices: cachedPrices, raw: null, fromCache: true, error: err };
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function getCachedPrices() {
  return cachedPrices;
}

export function usdToCoin(usdAmount, ticker) {
  if (!cachedPrices) return 0;
  const key = normalizeTicker(ticker);
  if (key === "USDT" || key === "USDC") return Number(usdAmount);
  const entry = cachedPrices[key];
  if (!entry?.usd) return 0;
  return Number(usdAmount) / entry.usd;
}

export function coinToUsd(coinAmount, ticker) {
  if (!cachedPrices) return 0;
  const key = normalizeTicker(ticker);
  if (key === "USDT" || key === "USDC") return Number(coinAmount);
  const entry = cachedPrices[key];
  if (!entry?.usd) return 0;
  return Number(coinAmount) * entry.usd;
}

export function getPrice(ticker) {
  if (!cachedPrices) return 0;
  const key = normalizeTicker(ticker);
  if (key === "USDT") return cachedPrices.USDT?.usd ?? 1;
  if (key === "USDC") return cachedPrices.USDC?.usd ?? 1;
  return cachedPrices[key]?.usd ?? 0;
}

export function get24hChange(ticker) {
  if (!cachedPrices) return 0;
  return cachedPrices[normalizeTicker(ticker)]?.usd_24h_change ?? 0;
}
